import { useCallback, useMemo } from "react";
import { matchPath, useLocation, useParams } from "react-router";
import rtkApi from "store/api";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import { EtudeTypes } from "../constants";
import { useAppSelector } from "./../../../store/hooks";
import { getSolutionID, getSolutionIDByCode } from "./../Solutions/SolutionDetails/helpers";
import {
  useGetSolutionsQuery,
  InstallationType,
} from "./useGetSolutionsQuery";
import { APP_URL } from "utils/defines";
import { patchMissingLocation } from "utils/stationMapping";

export const useCurrentEtudeData = () => {
  const { selectedDetail, etudeId: etudeParam, etudeType } = useParams();
  const { pathname } = useLocation();
  const { network } = useSalesNetwork();
  const { etudeData, etudeId, solutions } = useAppSelector(
    (state) => state.etude
  );

  const { isSolutionsFetching } = useGetSolutionsQuery();
  const { isFetching: isEtudeFetching } =
    rtkApi.endpoints.getEtude.useQueryState(
      {
        id: etudeId as number,
        code: network?.code,
      },
      {
        skip: !network || isNaN(Number(etudeId)) || !!etudeData || !etudeId,
      }
    );
  const { json, installationType, solutionRequestBody } = useMemo(() => {
    const isDocumentPage = matchPath(
      {
        path: `${APP_URL.Base}/${APP_URL.Etude}/${APP_URL.SolutionDetailDocuments}`,
        end: false,
      },
      pathname
    );
    const json = etudeData?.json ? JSON.parse(etudeData?.json) : null;
    if (json) patchMissingLocation(json);
    if (
      json &&
      (!selectedDetail || json?.selectedSolutionID != selectedDetail) &&
      !isDocumentPage
    ) {
      if (json.accessoires) json.accessoires = null;
      if (json.selectedSolutionID) json.selectedSolutionID = null;
      if (json.selectedSolution) json.selectedSolution = null;
    }
    const { installationType, solutionRequestBody } = json || {};

    return { json, installationType, solutionRequestBody };
  }, [etudeData, selectedDetail, pathname]);

  const solution = useMemo(() => {
    if (selectedDetail && solutions?.length) {

      return solutions.find((s) => getSolutionID(s) === selectedDetail || (json?.selectedSolution && getSolutionIDByCode(s) === getSolutionIDByCode(json?.selectedSolution)));
    } else if (json?.selectedSolutionID) {
      return solutions.find(
        (s) => getSolutionID(s) === json.selectedSolutionID || (json?.selectedSolution && getSolutionIDByCode(s) === getSolutionIDByCode(json.selectedSolution))
      ) as Solution;
    }
    return null;
  }, [selectedDetail, json, solutions]);

  const hasFXBallon = useCallback((targetSolution?: Solution | null) => {
    const currentSolution = targetSolution ?? solution;
    const ballons = (currentSolution?.ballons as any);

    return Array.isArray(ballons)
      ? ballons.some((b: any) => b?.code === "FXBALLON")
      : ballons?.primaires?.some((b: any) => b?.code === "FXBALLON") ||
      ballons?.secondaires?.some((b: any) => b?.code === "FXBALLON");
  }, [solution]);

  const result = useMemo(() => {
    return {
      service: etudeType,
      hasEcs: etudeType?.toLocaleLowerCase().includes(EtudeTypes.ECS),
      hasChauffage: etudeType?.includes(EtudeTypes.chauffage),
      isLoading: isSolutionsFetching || isEtudeFetching,
      hasTcoupure: json?.TcoupureQuestion && json?.Tcoupure != null,
      hasFXBallon,
      ...(etudeId == etudeParam
        ? {
          installationType,
          selectedDetail:
            selectedDetail || (solution ? getSolutionID(solution) : ""),
          etudeId,
          json,
          etudeData,
          solution,
          activeSolutions: solutions.filter((s) => {
            const isPacActive = s.pac?.thercodim_active !== false;

            const isEcsActive =
              s.ecs?.echangeur?.thercodim_active !== false &&
              (s.ecs?.ballons_stockage_ecs?.every(
                (e: any) => e.thercodim_active !== false,
              ) ??
                true);

            let areHeatingBallonsActive = true;

            if (installationType === InstallationType.electric) {
              const ballons = s.ballons as any; // ElectricBallons
              areHeatingBallonsActive =
                (ballons.primaires?.every(
                  (b: any) => b.thercodim_active !== false,
                ) ??
                  true) &&
                (ballons.secondaires?.every(
                  (b: any) => b.thercodim_active !== false,
                ) ??
                  true);
            } else if (installationType === InstallationType.hybride) {
              const ballons = s.ballons as any;

              const list = Array.isArray(ballons)
                ? ballons
                : ballons?.primaires
                  ? [ballons.primaires]
                  : [];

              areHeatingBallonsActive =
                list.every((b: any) => b?.thercodim_active !== false) ?? true;
            }

            if (etudeType === EtudeTypes.chauffage) {
              return isPacActive && areHeatingBallonsActive;
            } else if (etudeType === EtudeTypes.ECS) {
              return isEcsActive;
            } else {
              // chauffageECS or any other
              return isPacActive && isEcsActive && areHeatingBallonsActive;
            }
          }),
          solutionRequestBody,

        }
        : {
          etudeId: etudeParam,
          installationType: undefined,
          etudeData: undefined,
          json: undefined,
          solution: undefined,
          activeSolutions: [],
          solutionRequestBody: undefined,
          selectedDetail: undefined,
        }),
    };
  }, [
    installationType,
    selectedDetail,
    etudeId,
    json,
    etudeData,
    solution,
    solutions,
    solutionRequestBody,
    etudeParam,
    isSolutionsFetching,
    isEtudeFetching,
    hasFXBallon,

  ]);

  return result;
};
